(function (global, factory) {
  'use strict';

  if (typeof define === 'function' && define.amd) {
    // If supports for AMD is available return the value to the AMD Loader
    // and to the global (window) object in case any JS module is expecting
    // the SearchBarComponentModule to exists on the window object.
    define([], () => {
      global.SearchBarComponent = factory(global);
      return global.SearchBarComponent;
    });
  } else if (typeof exports === 'object') {
    // If supports for Common JS is available export the module
    // and pass its value to the global (window) object in case non Common JS code
    // is expected to work with this SearchBarComponentModule.
    const handler = factory(global);
    global.SearchBarComponent = handler;
    module.exports = handler;
  } else {
    // If not support for AMD or Common JS is available register the module in the global scope.
    global.SearchBarComponent = factory(global);
  }
}(typeof window !== 'undefined' ? window : {}, (global) => {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  const previousComponent = global.NewHomeSource.SearchBarComponent;

  /**
   * SearchBarComponent
   * Handles search input interactions in a reusable way.
   * Provides methods for event binding, input handling, and noConflict support.
   */
  class SearchBarComponent {
    /**
     * Creates a new SearchBarComponent instance.
     * Expects already queried DOM nodes (no internal querying done here).
     *
     * @param {Object} config - Configuration object.
     * @param {HTMLElement} config.searchBarContainer - Root element that visually/groups the search bar.
     * @param {HTMLInputElement} config.searchInput - Text input element for entering search terms.
     * @param {HTMLElement} config.resetButton - Button used to clear the search input (its hidden prop is toggled).
     */
    constructor({
      searchBarContainer,
      searchInput,
      resetButton,
      submitButton,
      searchBarInputHighlightContainer,
      lastSearchedText = ''
    }) {
      if (!searchBarContainer || !searchInput || !resetButton) {
        return;
      }

      this.searchBarContainer = searchBarContainer;
      this.searchInput = searchInput;
      this.resetButton = resetButton;
      this.submitButton = submitButton;
      this.searchBarInputHighlightContainer = searchBarInputHighlightContainer;
      this.lastSearchedText = lastSearchedText;
      this.searchText = '';

      this.bindEvents();
      this.updateInputValue();
    }

    /**
     * Binds event listeners to the search input and buttons.
     * Uses debouncing for input events to optimize performance.
     * Binds context to event handlers to ensure correct 'this' reference.
     */
    bindEvents() {
      this.debouncedHandleSearchInput = SearchBarComponent.debounce(this.handleSearchInput, 300, this);
      this.boundResetClick = this.handleResetButtonClick.bind(this);
      this.boundFocus = this.handleFocus.bind(this);
      this.boundBlur = this.handleBlur.bind(this);

      this.searchInput.addEventListener('input', this.debouncedHandleSearchInput);
      this.searchInput.addEventListener('focus', this.boundFocus);
      this.searchInput.addEventListener('blur', this.boundBlur);
      this.resetButton.addEventListener('click', this.boundResetClick);
    }

    /**
     * Cleans up event listeners to prevent memory leaks.
     */
    destroyEvents() {
      this.searchInput.removeEventListener('input', this.debouncedHandleSearchInput);
      this.searchInput.removeEventListener('focus', this.boundFocus);
      this.searchInput.removeEventListener('blur', this.boundBlur);

      this.resetButton.removeEventListener('click', this.boundResetClick);
    }

    /**
     * Updates input value to the last searched text when the current search is empty
     * @returns {boolean} Whether the input value was updated
     */
    updateInputValue() {
      if (!this.hasSearchText() && this.lastSearchedText) {
        this.setSearchText(this.lastSearchedText);
        return true;
      }
      return false;
    }

    /**
     * Sets search text and updates related UI elements
     * @param {string} text - The text to set
     */
    setSearchText(text) {
      this.searchText = text;
      if (this.searchInput) {
        this.searchInput.value = text;
      }

      this.toggleResetButtonVisibility();
      this.toggleInputHighlight();
    }

    /**
     * Checks if the search input has any non-whitespace text.
     * @returns {boolean} True if there is text, false otherwise.
     */
    hasSearchText() {
      return this.searchText.trim().length > 0;
    }

    /**
     * Handles the focus event on the search input.
     */
    handleFocus() {
      if (this.searchBarInputHighlightContainer) {
        this.toggleInputHighlight(true);
      }
    }

    /**
     * Handles the blur event on the search input.
     */
    handleBlur() {
      if (this.searchBarInputHighlightContainer && !this.hasSearchText()) {
        this.toggleInputHighlight(false);
      }
    }

    /**
     * Handles the input event on the search input.
     */
    handleSearchInput(event) {
      this.searchText = event.target.value;
      this.toggleResetButtonVisibility();
      this.toggleInputHighlight();
    }

    /**
     * Handles the click event on the reset button.
     * Clears the search input and notifies of the change if there was text.
     */
    handleResetButtonClick() {
      if (this.hasSearchText()) {
        this.setSearchText('');
        this.notifySearchChanged();
      }
    }

    /**
     * Notifies that the search input has changed by dispatching an 'input' event.
     */
    notifySearchChanged() {
      const event = new Event('input', { bubbles: true });
      this.searchInput.dispatchEvent(event);
    }

    /**
     * toggles the input highlight state.
     * @param {boolean} force - If true, forces the highlight to be active.
    */
    toggleInputHighlight(force = false) {
      if (this.searchBarInputHighlightContainer) {
        const isActive = force || this.hasSearchText();
        this.searchBarInputHighlightContainer.classList.toggle('nhs-n1-c-search-bar--active', isActive);
      }
    }

    /**
     * Toggles the visibility of the reset button based on the presence of search text.
     */
    toggleResetButtonVisibility() {
      if (this.resetButton) {
        this.resetButton.hidden = !this.hasSearchText();
      }
    }

    /**
     * debounces a function to limit how often it can be called.
     * @param {Function} func - The function to debounce.
     * @param {number} wait - The number of milliseconds to wait before allowing the next call.
     * @param {Object} context - The context to bind the function to.
     * @returns {Function} A debounced version of the input function.
    */
    static debounce(func, wait = 300, context = null) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
      };
    }

    /**
     * Creates an observable for a given DOM event.
     * @param {HTMLElement} element - The DOM element to observe.
     * @param {string} eventName - The name of the event to observe.
     * @param {Function} [transformFn] - Optional function to transform
     * the event object before passing it to subscribers.
     * @returns {Object} An observable with a subscribe method.
    */
    static createObservable(element, eventName, transformFn = (e) => e) {
      return {
        subscribe: (observerFn) => {
          const handler = (e) => observerFn(transformFn(e));
          element.addEventListener(eventName, handler);
          return () => element.removeEventListener(eventName, handler);
        }
      };
    }

    /**
     * Creates an observable for input change events.
     * @returns {Object} Observable for input change events, emitting the current input value.
     */
    getInputChangeObservable() {
      return SearchBarComponent.createObservable(
        this.searchInput,
        'input',
        () => ({
          container: this.searchBarContainer,
          value: this.searchInput.value
        })
      );
    }

    /**
     * Creates an observable for input focus events.
     * @returns {Object} Observable for input focus events.
     */
    getInputFocusObservable() {
      return SearchBarComponent.createObservable(
        this.searchInput,
        'focus',
        () => ({
          container: this.searchBarContainer,
          value: this.searchInput.value
        })
      );
    }

    /**
     * Creates an observable for submit button click events.
     * @returns {Object} Observable for submit button click events.
     */
    getSubmitButtonClickObservable() {
      if (this.submitButton) {
        return SearchBarComponent.createObservable(this.submitButton, 'click');
      }

      return {
        subscribe: () => () => { } // subscribe() returns an unsubscribe function
      };
    }

    /**
     * Restores the previous definition of SearchBarComponent.
     * Useful when integrating with other scripts that might use the same namespace.
     * @returns {SearchBarComponent} The current SearchBarComponent class
     */
    static noConflict() {
      global.NewHomeSource.SearchBarComponent = previousComponent;
      return SearchBarComponent;
    }
  }

  global.NewHomeSource.SearchBarComponent = SearchBarComponent;
  return SearchBarComponent;
}));
